"use client"

import { useNavigate } from '../lib/next-router-compat'
import { Button, Card } from '@heroui/react'
import { EmptyState, KPI, NumberValue, Timeline } from '@heroui-pro/react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  BriefcaseBusiness,
  CheckCircle2,
  Clock3,
  Plus,
} from 'lucide-react'
import { api } from '../lib/api'
import type { Deal } from '../lib/types'
import { isDealAtRisk, isOpenDealStatus } from '../lib/view'
import { ClosingPipelineCard } from '../features/dashboard/ClosingPipelineCard'
import { PortfolioMixCard } from '../features/dashboard/PortfolioMixCard'
import { PriorityDealList, getClosingSoonCount, sortByUpcomingClose } from '../features/dashboard/PriorityDealList'

export function DashboardPage(): React.ReactElement {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['deals'],
    queryFn: ({ signal }) => api.deals.list(signal),
  })

  const deals = data?.deals ?? []
  const active = deals.filter((deal) => isOpenDealStatus(deal.status)).length
  const atRisk = deals.filter(isDealAtRisk).length
  const closingSoon = getClosingSoonCount(deals)
  const totalValue = deals.reduce((sum, deal) => sum + (deal.purchase_price ?? 0), 0)
  const recentDeals = sortByUpcomingClose(deals).slice(0, 5)

  return (
    <div className="page-shell flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KPI>
          <KPI.Header>
            <KPI.Title>Active Deals</KPI.Title>
          </KPI.Header>
          <KPI.Content>
            <KPI.Value value={isLoading ? 0 : active} />
            <KPI.Trend trend="neutral">{deals.length} total</KPI.Trend>
          </KPI.Content>
        </KPI>

        <KPI>
          <KPI.Header>
            <KPI.Title>At Risk</KPI.Title>
          </KPI.Header>
          <KPI.Content>
            <KPI.Value value={isLoading ? 0 : atRisk} />
            <KPI.Trend trend={atRisk > 0 ? 'down' : 'neutral'}>{atRisk > 0 ? 'needs review' : 'clear'}</KPI.Trend>
          </KPI.Content>
        </KPI>

        <KPI>
          <KPI.Header>
            <KPI.Title>Closing Soon</KPI.Title>
          </KPI.Header>
          <KPI.Content>
            <KPI.Value value={isLoading ? 0 : closingSoon} />
            <KPI.Trend trend="neutral">5-day window</KPI.Trend>
          </KPI.Content>
        </KPI>

        <KPI>
          <KPI.Header>
            <KPI.Title>Volume</KPI.Title>
          </KPI.Header>
          <KPI.Content>
            <KPI.Value currency="USD" maximumFractionDigits={0} notation="compact" style="currency" value={isLoading ? 0 : totalValue} />
            <KPI.Trend trend="neutral">portfolio</KPI.Trend>
          </KPI.Content>
        </KPI>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_380px]">
        <ClosingPipelineCard deals={deals} isLoading={isLoading} />
        <PortfolioMixCard deals={deals} isLoading={isLoading} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_380px]">
        <Card className="overflow-hidden bg-surface">
          <Card.Header className="flex-row items-center justify-between gap-3">
            <div>
              <Card.Title className="text-base font-semibold text-foreground">Priority Deals</Card.Title>
              <Card.Description className="text-sm text-muted">Sorted by closing-date pressure</Card.Description>
            </div>
            <Button size="sm" variant="outline" onPress={() => { void navigate('/deals') }}>
              View All
            </Button>
          </Card.Header>
          <Card.Content className="px-3 pb-3">
            {!isLoading && recentDeals.length === 0 ? (
              <EmptyState className="min-h-[320px] rounded-lg border border-dashed border-separator" size="lg">
                <EmptyState.Header>
                  <EmptyState.Media variant="icon">
                    <BriefcaseBusiness className="size-5" />
                  </EmptyState.Media>
                  <EmptyState.Title>No Deals Yet</EmptyState.Title>
                  <EmptyState.Description>New transaction files will land here.</EmptyState.Description>
                </EmptyState.Header>
                <EmptyState.Content>
                  <Button onPress={() => { void navigate('/deals/new') }}>
                    <Plus className="size-4" />
                    New Deal
                  </Button>
                </EmptyState.Content>
              </EmptyState>
            ) : (
              <PriorityDealList deals={recentDeals} />
            )}
          </Card.Content>
        </Card>

        <div className="flex flex-col gap-6">
          <Card className="bg-surface">
            <Card.Header>
              <Card.Title className="text-base font-semibold text-foreground">Desk Rhythm</Card.Title>
              <Card.Description className="text-sm text-muted">Today's operating cadence</Card.Description>
            </Card.Header>
            <Card.Content>
              <Timeline density="compact" size="sm">
                <Timeline.Item status={atRisk > 0 ? 'danger' : 'success'}>
                  <Timeline.Marker>
                    {atRisk > 0 ? <AlertTriangle className="size-3.5" /> : <CheckCircle2 className="size-3.5" />}
                  </Timeline.Marker>
                  <Timeline.Content>
                    <h3 className="m-0 text-sm font-medium text-foreground">Risk Review</h3>
                    <p className="m-0 text-sm text-muted">{atRisk > 0 ? `${atRisk} deal${atRisk === 1 ? '' : 's'} need attention` : 'No urgent closing pressure'}</p>
                  </Timeline.Content>
                </Timeline.Item>
                <Timeline.Item status={closingSoon > 0 ? 'warning' : 'default'}>
                  <Timeline.Marker>
                    <Clock3 className="size-3.5" />
                  </Timeline.Marker>
                  <Timeline.Content>
                    <h3 className="m-0 text-sm font-medium text-foreground">Closing Window</h3>
                    <p className="m-0 text-sm text-muted">{closingSoon} deal{closingSoon === 1 ? '' : 's'} inside five days</p>
                  </Timeline.Content>
                </Timeline.Item>
                <Timeline.Item status="current">
                  <Timeline.Marker>
                    <BriefcaseBusiness className="size-3.5" />
                  </Timeline.Marker>
                  <Timeline.Content>
                    <h3 className="m-0 text-sm font-medium text-foreground">File Intake</h3>
                    <p className="m-0 text-sm text-muted">
                      <NumberValue value={deals.length} /> transaction{deals.length === 1 ? '' : 's'} in workspace
                    </p>
                  </Timeline.Content>
                </Timeline.Item>
              </Timeline>
            </Card.Content>
          </Card>
        </div>
      </div>
    </div>
  )
}
